import { TrashIcon, ArrowUpIcon, QrcodeIcon, RefreshIcon } from '@heroicons/react/outline'
import { ArrowDownIcon } from '@heroicons/react/solid'
import CardV2 from 'components/Card/CardV2'
import DashboardLayout from 'components/Layouts/DashboardLayout'
import TransactionReceipt from 'components/TransactionReceipt'
import currencyFormatter from 'currency-formatter'
import useAuth from 'hooks/useAuth'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { extractNumber } from 'utils/extractNumber'

interface HistoryItem {
  date: string
  type: string
  amount: string
  amountReceived: string
}

const Dashboard = () => {
  const auth = useAuth()
  const navigate = useNavigate()
  const [isSender] = useState(localStorage.getItem('token') === 'sender')
  const [history, setHistory] = useState<HistoryItem[]>(
    isSender ? auth.history() : [],
  )
  const [selectedTx, setSelectedTx] = useState<
    { date: string; amount: string } | undefined
  >(undefined)
  const [pollCtr, setPollCtr] = useState(0)

  const calcReceiverBalance = (history: any) => {
    let total: number = 0
    history.forEach((item) => {
      if (item.type === 'Received' && item.amountReceived) {
        const extractedNumber = extractNumber(item.amountReceived)
        if (extractedNumber !== null) {
          total += extractedNumber
        }
      }
    })
    auth.setReceiverBalance(total)
  }

  useEffect(() => {
    if (!auth.isAuth()) {
      navigate('/')
    }
  }, [auth])

  useEffect(() => {
    if (isSender) return
    ;(async () => {
      try {
        const res = await fetch(
          'https://cllmkj2upa.execute-api.us-east-1.amazonaws.com/receiver-history',
        )
        const data = await res.json()
        const sortedData = data.sort((a: any, b: any) => {
          return new Date(b.date).getTime() - new Date(a.date).getTime()
        })
        calcReceiverBalance(sortedData)
        setHistory(sortedData)
      } catch (e) {
        console.error(e)
      }
    })()

    const timer = setTimeout(() => {
      setPollCtr(pollCtr + 1)
    }, 2000)

    return () => clearTimeout(timer)
  }, [pollCtr])

  const clearHistory = async () => {
    try {
      await fetch(
        'https://cllmkj2upa.execute-api.us-east-1.amazonaws.com/receiver-history',
        { method: 'DELETE' },
      )
      setHistory([])
    } catch (e) {
      console.error(e)
    }
  }

  const getTransactionIcon = (type: string) => {
    if (type === 'Top Up') return <ArrowDownIcon className="w-4 h-4 text-success-600" />
    if (type === 'Payment') return <ArrowUpIcon className="w-4 h-4 text-error-600" />
    if (type === 'Received') return <ArrowDownIcon className="w-4 h-4 text-success-600" />
    return <RefreshIcon className="w-4 h-4 text-neutral-400" />
  }

  const getAmountColor = (amount: string) => {
    if (amount.startsWith('+')) return 'text-success-600'
    if (amount.startsWith('-')) return 'text-error-600'
    return 'text-neutral-800'
  }

  return (
    <DashboardLayout>
      <div className="mb-8">
        <h1 className="heading-2 mb-2">Welcome back</h1>
        <p className="text-neutral-500">Manage your money with ease</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card p-6 bg-gradient-to-br from-primary-500 to-primary-600 text-white">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-primary-100 text-sm font-medium mb-1">Total Balance</p>
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
                  {currencyFormatter.format(auth.balance(), {
                    symbol: isSender ? 'SGD' : 'PHP',
                    format: '%s %v',
                  })}
                </h2>
              </div>
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-primary-100">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span>Account active</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/topup/enter-amount')}
              className="card card-hover p-6 text-left group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-success-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <ArrowDownIcon className="w-6 h-6 text-success-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-800">Top Up</h3>
                  <p className="text-sm text-neutral-500">Add funds to wallet</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/payment')}
              className="card card-hover p-6 text-left group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <QrcodeIcon className="w-6 h-6 text-secondary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-800">Pay / Receive</h3>
                  <p className="text-sm text-neutral-500">QR payments</p>
                </div>
              </div>
            </button>
          </div>
        </div>

        <div className="lg:col-span-1">
          <CardV2 title="Recent Activity" className="h-full">
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {history.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                    <RefreshIcon className="w-8 h-8 text-neutral-400" />
                  </div>
                  <p className="text-neutral-500">No transactions yet</p>
                </div>
              )}
              {history.map((item, key) => (
                <button
                  key={key}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-neutral-50 transition-colors text-left"
                  onClick={() => {
                    if (item.type === 'Payment' || item.type === 'Received') {
                      setSelectedTx({
                        date: item.date,
                        amount: item.amountReceived !== '' ? item.amountReceived : item.amount,
                      })
                    }
                  }}
                >
                  <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
                    {getTransactionIcon(item.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-neutral-800 text-sm">{item.type}</p>
                    <p className="text-xs text-neutral-400 truncate">
                      {isSender
                        ? item.date
                        : new Date(item.date).toLocaleString('en-US', auth.formatOptions)}
                    </p>
                  </div>
                  <span className={`font-semibold text-sm ${getAmountColor(item.amount)}`}>
                    {item.amount}
                  </span>
                </button>
              ))}
            </div>
            {!isSender && history.length > 0 && (
              <button
                onClick={clearHistory}
                className="mt-4 flex items-center gap-2 text-sm text-error-600 hover:text-error-700 transition-colors"
              >
                <TrashIcon className="w-4 h-4" />
                Clear history
              </button>
            )}
          </CardV2>
        </div>
      </div>

      <TransactionReceipt
        isVisible={selectedTx !== undefined}
        onClose={() => setSelectedTx(undefined)}
        tx={selectedTx}
      />
    </DashboardLayout>
  )
}

export default Dashboard
